import { useSnackbar } from 'notistack';
import * as React from 'react';
import * as Yup from 'yup';
import { RequestCommandSudo, useCommandSudo } from '../../../../../../../../app/ApiGen';
import { Dialog, DialogProps } from '../../../../../../../../components/Dialog';
import { SelectionMenu } from '../../../../../../../../components/SelectionMenu';
import { TextField } from '../../../../../../../../components/TextField';
import { Form, OnSubmit } from '../../../../../../../../design-system';
import { createSchema } from '../../../../../../../../utils/formUtils';
import { useChargePoint } from '../../../Provider';
import { useHandleError } from './useHandleError';

const initialValues = {
  action: '',
  message: '',
};

type SudoDialogFields = {
  action: string;
  message: string;
};

const validationSchema = createSchema<SudoDialogFields>({
  action: Yup.string().required('Action is required'),
});

export const SudoDialog: React.FC<DialogProps> = ({ isOpen, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { chargePoint, refetch } = useChargePoint();
  const { error, mutate } = useCommandSudo({ id: chargePoint?.id ?? '' });

  useHandleError({ error, defaultErrorMessage: 'Unable to send the custom OCPP message' });

  const onSubmit = React.useCallback<OnSubmit<SudoDialogFields>>(
    async ({ action, message }, { setSubmitting }) => {
      try {
        const { data } = await mutate({
          action: action as RequestCommandSudo['action'],
          message: message ? (JSON.parse(message) as RequestCommandSudo['message']) : {},
        });
        enqueueSnackbar(`OCPP Action ${data.status.toLocaleLowerCase()}`);
        onClose();
        refetch();
      } finally {
        setSubmitting(false);
      }
    },
    [mutate, enqueueSnackbar, onClose, refetch],
  );

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="OCPP Action">
      <Form<SudoDialogFields>
        fields={[
          {
            id: 'action',
            label: 'OCPP Action',
            inputComponent: ({ disabled, error: actionFieldError, name, onBlur, onChange }) => (
              <SelectionMenu
                disabled={disabled}
                error={actionFieldError}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
                options={[
                  { value: 'CancelReservation' },
                  { value: 'ChangeAvailability' },
                  { value: 'ChangeConfiguration' },
                  { value: 'ClearCache' },
                  { value: 'ClearChargingProfile' },
                  { value: 'DataTransfer' },
                  { value: 'DeleteCertificate' },
                  { value: 'GetCompositeSchedule' },
                  { value: 'GetDiagnostics' },
                  { value: 'GetInstalledCertificateIds' },
                  { value: 'GetLocalListVersion' },
                  { value: 'GetLog' },
                  { value: 'InstallCertificate' },
                  { value: 'RemoteStartTransaction' },
                  { value: 'RemoteStopTransaction' },
                  { value: 'ReserveNow' },
                  { value: 'Reset' },
                  { value: 'SendLocalList' },
                  { value: 'SetChargingProfile' },
                  { value: 'TriggerMessage' },
                  { value: 'UnlockConnector' },
                  { value: 'UpdateFirmware' },
                ]}
              />
            ),
          },
          {
            id: 'message',
            label: 'Message',
            inputComponent: ({ disabled, error: messageFieldError, helperText, name, onBlur, onChange }) => (
              <TextField
                defaultValue=""
                disabled={disabled}
                error={messageFieldError}
                fullWidth
                helperText={helperText}
                name={name}
                onBlur={onBlur}
                onChange={onChange}
              />
            ),
          },
        ]}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        labels={{ save: 'Send command' }}
        isEditing
      />
    </Dialog>
  );
};
